import { MouseEvent, useState } from 'react';
import cn from 'classnames';

import RecosProductDetail from 'components/outfitRecos/RecosProductDetail';
import { OutfitRecoContent, RecoCardProduct } from 'types/outfitRecos';
import MelodyModal from 'components/common/MelodyModal';
import { constructMSAImageUrl } from 'helpers/index';
import { ProductBundleOptions, ProductStyle } from 'types/cloudCatalog';
import { isLeftClickEvent, isModifiedEvent } from 'helpers/EventHelpers';
import useMartyContext from 'hooks/useMartyContext';
import { OUTFIT_RECOS_QUICK_VIEW } from 'constants/amethystPageTypes';
import { track } from 'apis/amethyst';
import { evOutfitRecoModalCarouselClick, evOutfitRecoModalViewDetailsClick } from 'events/outfitRecos';
import { generateOutfitGroup } from 'helpers/outfitRecoUtils';

import css from 'styles/components/outfitRecos/recosQuickViewModal.scss';

type RecosQuickViewModalProps = {
  outfitRecoContent: OutfitRecoContent;
  closeModal: () => void;
  initialStyleId?: string;
  currentStyle: ProductStyle;
};

type InlineRecoProductProps = {
  product: RecoCardProduct;
  onClick: () => void;
  itemCss?: string;
};

type OutfitContentProps = {
  recoContent: OutfitRecoContent;
  initialStyleId?: string;
  currentStyle: ProductStyle;
};

type RecoProductSelectorProps = {
  productList: RecoCardProduct[];
  changeProduct: (product: RecoCardProduct) => void;
  selectedStyleId: string;
};

export const InlineRecoProduct = (props: InlineRecoProductProps) => {
  const {
    product: { brand, productName, imageId },
    itemCss,
    onClick
  } = props;

  const imageDimensions = { width: 256, height: 256, autoCrop: true };

  const { testId } = useMartyContext();
  const label = brand ? `View ${brand} ${productName}` : 'View current product';
  const defaultPreventingClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    if (isLeftClickEvent(e) && !isModifiedEvent(e)) {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <button
      type="button"
      className={itemCss}
      onClick={defaultPreventingClickHandler}
      aria-label={label}
      rel="nofollow"
      data-test-id={testId('outfitRecoQuickViewProduct')}
    >
      <img alt={label} src={constructMSAImageUrl(imageId, imageDimensions)} />
    </button>
  );
};

const RecoProductSelector = ({ changeProduct, selectedStyleId, productList }: RecoProductSelectorProps) => (
  <div className={css.outfitSelector}>
    <h2 className={css.outfitHeading}>Styling Ideas</h2>
    <div className={css.itemContainer}>
      {productList.map(product => (
        <InlineRecoProduct
          onClick={() => changeProduct(product)}
          itemCss={cn(css.item, { [css.selected]: selectedStyleId === product.styleId })}
          key={product.styleId}
          product={product}
        />
      ))}
    </div>
  </div>
);

const OutfitContent = ({ recoContent, initialStyleId, currentStyle }: OutfitContentProps) => {
  const [selectedStyleId, setSelectedStyleId] = useState(initialStyleId);

  const { productList, complementaryGroupId } = recoContent;
  const { styleId: seedStyleId } = currentStyle;

  if (!initialStyleId) {
    return null;
  }

  const changeProduct = (product: RecoCardProduct) => {
    const { styleId: clickStyleId } = product;
    setSelectedStyleId(clickStyleId);
    const outfitGroup = generateOutfitGroup(complementaryGroupId, productList);
    track(() => [evOutfitRecoModalCarouselClick, { seedStyleId, outfitGroup, clickStyleId }]);
  };

  const product = productList.find(product => product.styleId === selectedStyleId);
  let productQueryOpts: ProductBundleOptions | undefined;
  if (product) {
    productQueryOpts = {
      productId: product.productId
    };
  }

  const onViewDetailsClick = () => {
    const outfitGroup = generateOutfitGroup(complementaryGroupId, productList);
    track(() => [evOutfitRecoModalViewDetailsClick, { seedStyleId, outfitGroup, clickStyleId: selectedStyleId }]);
  };

  return (
    <>
      <RecoProductSelector productList={productList} changeProduct={changeProduct} selectedStyleId={selectedStyleId!} />
      {product && productQueryOpts && (
        <RecosProductDetail
          id="outfit-productDetail"
          productQuery={productQueryOpts}
          initialStyleId={product.styleId}
          pageType={OUTFIT_RECOS_QUICK_VIEW}
          onViewDetailsClick={onViewDetailsClick}
        />
      )}
    </>
  );
};

export const RecosQuickViewModal = ({ closeModal, outfitRecoContent, initialStyleId, currentStyle }: RecosQuickViewModalProps) => (
  <MelodyModal className={css.modal} isOpen={true} onRequestClose={closeModal} contentLabel="Outfit Quick View" wrapperTestId="outfitQuickViewModal">
    <OutfitContent recoContent={outfitRecoContent} initialStyleId={initialStyleId} currentStyle={currentStyle} />
  </MelodyModal>
);

export default RecosQuickViewModal;
